import {Nullable, PriceType, StockInformationType} from "../../definitions/types";
import {PriceModel} from "../domain/priceModel";

export class PricingModel {
    private readonly _pricing: PriceModel[];

    /**
     * @constructor
     * @param {PriceType[]} pricing - The prices for the product in the current currency sorted by low range ascending
     * @param stockInformation {Nullable<StockInformationType>}
     * @description Creates a new pricing model from the prices returned from the API
     */
    constructor(public readonly pricing: PriceType[], public readonly stockInformation: Nullable<StockInformationType>) {
        this._pricing = pricing
            .sort((a, b) => a.lowRange - b.lowRange)
            .map(priceType => new PriceModel(priceType, stockInformation));
    }

    /**
     * @method
     * @description Returns the prices for the product in the current currency sorted by low-range ascending
     * @returns {PriceModel[]}
     */
    public get prices(): PriceModel[] {
        return this._pricing;
    }

    /**
     * @method
     * @description Returns the starting price for the product in the current currency
     * @returns {PriceModel | undefined}
     */
    public get startingPrice(): PriceModel | undefined {
        return this.prices.length > 0 ? this.prices[0] : undefined;
    }

    /**
     * @method
     * @description Returns the ending price for the product in the current currency
     * @returns {PriceModel | undefined}
     */
    public get endingPrice(): PriceModel | undefined {
        return this.prices.length > 0 ? this.prices[this.prices.length - 1] : undefined;
    }

    /**
     * @method
     * @description Returns the lowest price for the product in the current currency
     * @returns {PriceModel | undefined}
     */
    public get lowestPrice(): PriceModel | undefined {
        return this.prices.length > 0 ? this.prices.sort((a, b) => a.unitPrice - b.unitPrice)[0] : undefined;
    }

    /**
     * @method
     * @description Returns the highest price for the product in the current currency
     * @returns {PriceModel | undefined}
     */
    public get highestPrice(): PriceModel | undefined {
        return this.prices.length > 0 ? this.prices.sort((a, b) => b.unitPrice - a.unitPrice)[0] : undefined;
    }

    /**
     * @method
     * @description Returns the number of prices for the product in the current currency
     * @returns {number}
     */
    public get count(): number {
        return this.prices.length;
    }

    /**
     * @property breakOutEarly
     * @description Returns true if there is only one price for the product in the current currency
     * @returns {boolean}
     */
    public get breakOutEarly(): boolean {
        return this.count < 2;
    }
}